/**
 * TechModeCard — a stripped-down "curbside sales" view.
 *
 * Designed for a locksmith techncian in the field who needs to quote
 * a customer in 5 seconds. Shows only the high-impact info:
 *   - Big retail price
 *   - Key blank part number (so they can grab it off the truck)
 *   - Cheapest available retailer (with affiliate link)
 *   - Recommended programmer
 *   - Estimated time
 *   - Profit
 *
 * Everything else is hidden behind the tabs below.
 */
import React from 'react';
import { Key, Wrench, Clock, DollarSign, ExternalLink, TrendingUp } from 'lucide-react';

function pickBestRetailer(pricing) {
  if (!Array.isArray(pricing) || pricing.length === 0) return null;
  const available = pricing.filter((p) => p.available !== false && p.price);
  if (available.length === 0) return null;
  return available.reduce((best, p) => (p.price < (best?.price ?? Infinity) ? p : best), null);
}

function withAffiliate(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    const affId = process.env.REACT_APP_AFFILIATE_ID || 'mykeyboss-20';
    if (u.hostname.includes('amazon.')) {
      if (!u.searchParams.get('tag')) u.searchParams.set('tag', affId);
    }
    if (!u.searchParams.get('utm_source')) u.searchParams.set('utm_source', 'mykeyboss');
    if (!u.searchParams.get('utm_medium')) u.searchParams.set('utm_medium', 'tech_mode');
    return u.toString();
  } catch (_) {
    return url;
  }
}

const fmt = (v) =>
  typeof v === 'number'
    ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v)
    : '—';

export default function TechModeCard({ vehicle }) {
  if (!vehicle) return null;

  const bestKey = pickBestRetailer(vehicle.key_blank_retailer_pricing);
  const programmer =
    vehicle.recommended_programmer ||
    (Array.isArray(vehicle.compatible_programmers) && vehicle.compatible_programmers[0]) ||
    null;
  const retail = vehicle.suggested_retail_price;
  const profit = vehicle.estimated_profit;
  const time = vehicle.estimated_time_minutes;
  const part = vehicle.key_blank_part_number || vehicle.key_blank_oem_part;

  return (
    <div
      className="rounded-3xl border-2 border-yellow-500/40 bg-gradient-to-br from-yellow-500/10 via-amber-500/5 to-orange-500/10 p-6 md:p-8 shadow-xl"
      data-testid="tech-mode-card"
    >
      <div className="flex items-center gap-2 mb-4">
        <span className="text-xs font-bold uppercase tracking-wider text-yellow-400 bg-yellow-500/10 px-3 py-1 rounded-full border border-yellow-500/30">
          ⚡ Tech Mode
        </span>
        <span className="text-xs text-muted-foreground">Curbside-ready quote</span>
      </div>

      {/* Retail price hero */}
      <div className="text-center mb-6">
        <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">
          Quote the customer
        </p>
        <p
          className="text-6xl md:text-7xl font-black text-green-400 leading-none"
          data-testid="tech-mode-retail"
        >
          {fmt(retail)}
        </p>
        {typeof profit === 'number' && (
          <p className="text-sm text-muted-foreground mt-2 flex items-center justify-center gap-1">
            <TrendingUp className="w-4 h-4 text-emerald-400" />
            Your profit:{' '}
            <span className="font-bold text-emerald-400 ml-1">{fmt(profit)}</span>
          </p>
        )}
      </div>

      {/* Action grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Tile
          icon={Key}
          color="text-cyan-400"
          label="Key Blank"
          value={part || '—'}
          subtext={
            vehicle.key_blank_ilco
              ? `ILCO ${vehicle.key_blank_ilco}`
              : vehicle.key_blank_material || null
          }
          testid="tech-mode-key-blank"
        />
        <Tile
          icon={Wrench}
          color="text-violet-400"
          label="Programmer"
          value={programmer || '—'}
          subtext={vehicle.programming_method || null}
          testid="tech-mode-programmer"
        />
        <Tile
          icon={Clock}
          color="text-sky-400"
          label="Time on Job"
          value={time ? `${time} min` : '—'}
          subtext={vehicle.chip_type ? `Chip: ${vehicle.chip_type}` : null}
          testid="tech-mode-time"
        />
      </div>

      {/* Best retailer */}
      {bestKey && (
        <div
          className="mt-4 rounded-2xl border border-green-500/30 bg-green-500/5 p-4 flex items-center justify-between flex-wrap gap-3"
          data-testid="tech-mode-best-retailer"
        >
          <div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">
              Cheapest in stock
            </div>
            <div className="font-bold text-lg">
              {bestKey.retailer} ·{' '}
              <span className="text-green-400">{fmt(bestKey.price)}</span>
            </div>
          </div>
          {bestKey.url && (
            <a
              href={withAffiliate(bestKey.url)}
              target="_blank"
              rel="noopener noreferrer sponsored"
              className="inline-flex items-center gap-1 px-4 py-2 rounded-lg bg-green-500/20 text-green-300 hover:bg-green-500/30 text-sm font-semibold"
              data-testid="tech-mode-buy-link"
            >
              Order Now <ExternalLink className="w-3.5 h-3.5" />
            </a>
          )}
        </div>
      )}

      {/* Cost breakdown — small print */}
      {typeof vehicle.locksmith_cost === 'number' && (
        <div className="mt-3 flex items-center justify-center gap-4 text-xs text-muted-foreground">
          <span>
            <DollarSign className="w-3 h-3 inline mr-0.5" />
            Your cost: <span className="text-foreground">{fmt(vehicle.locksmith_cost)}</span>
          </span>
        </div>
      )}
    </div>
  );
}

const Tile = ({ icon: Icon, color, label, value, subtext, testid }) => (
  <div
    className="rounded-2xl border border-border/60 bg-card/60 p-4 flex flex-col gap-1"
    data-testid={testid}
  >
    <div className={`flex items-center gap-2 text-xs uppercase tracking-wider ${color}`}>
      <Icon className="w-4 h-4" /> {label}
    </div>
    <div className="text-base md:text-lg font-bold leading-tight">{value}</div>
    {subtext && <div className="text-xs text-muted-foreground">{subtext}</div>}
  </div>
);
